import { Fragment, useContext, useState } from 'react'

import clsx from 'clsx'

import { useRetrainHistoryGetApi } from '@/common/api/modules/retrain'
import { retrainLabel } from '@/common/app/modules/recommend/retrain'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import { IconInversion, IconType } from '@/components/view/atoms/icon/Icon'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Modal from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { TrainingHistoryModalContext } from './TrainingHistoryModalProvider'

interface SnapshotHybridWeights {
  hybrid_weights?: {
    collaborative: number
    neural: number
    content_based: number
  }
}

const parseJsonField = <T,>(field: unknown, fallback: Record<string, unknown> = {}): T => {
  if (field === null || field === undefined) return fallback as T
  if (typeof field === 'object') return field as T
  if (typeof field === 'string') {
    try {
      return JSON.parse(field) as T
    } catch {
      return fallback as T
    }
  }
  return fallback as T
}

const ENGINE_NAMES: Record<string, string> = {
  collaborative: '協調フィルタリング',
  neural: 'ニューラル (NCF)',
  content_based: 'コンテンツベース',
  memory_mb: 'メモリ使用量'
}

const HistoryDetail = ({ detail }: { detail: string | Record<string, unknown> }) => {
  const parsed = parseJsonField<Record<string, unknown>>(detail)
  if (!parsed || Object.keys(parsed).length === 0) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        詳細情報なし
      </Text>
    )
  }

  return (
    <div className='flex w-full gap-3'>
      {Object.entries(parsed).map(([key, val]) => (
        <div
          key={key}
          className={clsx(
            'bg-surface w-full rounded-lg border p-3',
            key == 'content_based' && 'border-orange-300',
            key == 'collaborative' && 'border-sora-400',
            key == 'neural' && 'border-[#57C6B1]'
          )}
        >
          <Text size={12} weight='bold'>
            {ENGINE_NAMES[key] ?? key}
          </Text>
          <div className='mt-2 flex flex-col gap-1'>
            {typeof val === 'object' && val !== null ? (
              Object.entries(val as Record<string, unknown>).map(([k, v]) => (
                <ItemAlign key={k} gapX={4}>
                  <Badge badgeType={BadgeType.SQUARE_TERTIARY}>{retrainLabel(k)}</Badge>
                  <Text size={12}>
                    {typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(4)) : String(v)}
                  </Text>
                </ItemAlign>
              ))
            ) : (
              <Text size={12} color={TextColor.SECONDARY}>
                {String(val)}
              </Text>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const TrainingHistoryModalInner = ({ siteId }: { siteId: number }) => {
  const { data: historyData } = useRetrainHistoryGetApi({ site_id: siteId })
  const [expandedHistory, setExpandedHistory] = useState<number | null>(null)

  return (
    <ModalContent type={ModalContentType.FLEX_GROW}>
      {!historyData || historyData.length === 0 ? (
        <Text size={14} color={TextColor.SECONDARY}>
          まだ再学習が実行されていません。
        </Text>
      ) : (
        <table>
          <thead>
            <TableRow head noBorder>
              {['実行日時', 'トリガー', 'ステータス', '所要時間', '配分比率', ''].map((label) => (
                <TableHeadItem key={label} type='thin'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    {label}
                  </Text>
                </TableHeadItem>
              ))}
            </TableRow>
          </thead>
          <tbody>
            {historyData.map((h, i) => {
              const snapshot = parseJsonField<SnapshotHybridWeights>(h.tuning_params_snapshot)
              const hw = snapshot?.hybrid_weights
              const isExpanded = expandedHistory === h.id

              return (
                <Fragment key={h.id}>
                  <TableRow>
                    <TableRowItem>
                      <Text size={12}>
                        {h.created_at ? new Date(h.created_at).toLocaleString('ja-JP') : '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Badge
                        badgeType={
                          h.trigger === 'auto' ? BadgeType.SQUARE_TERTIARY : BadgeType.SECONDARY
                        }
                        size={12}
                      >
                        {h.trigger === 'auto' ? '自動' : '手動'}
                      </Badge>
                    </TableRowItem>
                    <TableRowItem>
                      <Badge
                        badgeType={
                          h.status === 'complete'
                            ? BadgeType.SUCCESS
                            : h.status === 'processing'
                              ? BadgeType.EMPHASIS
                              : BadgeType.CRITICAL
                        }
                        size={12}
                      >
                        {h.status === 'complete'
                          ? '成功'
                          : h.status === 'processing'
                            ? '処理中'
                            : 'エラー'}
                      </Badge>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={12}>
                        {h.duration_ms != null
                          ? h.duration_ms < 1000
                            ? `${h.duration_ms}ms`
                            : `${(h.duration_ms / 1000).toFixed(1)}s`
                          : '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {hw
                          ? `協調${(hw.collaborative * 100).toFixed(0)}% / NCF${(hw.neural * 100).toFixed(0)}% / CB${(hw.content_based * 100).toFixed(0)}%`
                          : '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <IconButton
                        onClick={() => setExpandedHistory(isExpanded ? null : h.id)}
                        type={IconType.ARROW_DOWN}
                        iconInversion={isExpanded ? IconInversion.VERTICAL : undefined}
                      />
                    </TableRowItem>
                  </TableRow>
                  {isExpanded && (
                    <tr key={`${h.id}-detail`}>
                      <td colSpan={7} className='bg-fill-secondary px-4 py-3'>
                        <HistoryDetail detail={h.result_detail} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </ModalContent>
  )
}

const TrainingHistoryModal = () => {
  const { modal, showing, hide } = useContext(TrainingHistoryModalContext)

  return (
    <Modal show={showing}>
      <ModalHeader text='再学習履歴' onClickClose={hide} />
      {modal.showing && <TrainingHistoryModalInner siteId={modal.siteId} />}
      <ModalFooter>
        <Button color='secondary' size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default TrainingHistoryModal
